import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { BASE_PATH, STATIC_CATALOG } from "./constants";
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import AnalyticsIdentityBridge from './util/AnalyticsIdentityBridge';

import { hasValue } from "./util/Functions";
import { DB } from "./util/Cache.util";
import CatalogContext from "./contexts/CatalogContext";
import LoadMask from "./components/LoadMask";

import { useAuth } from "./auth/useAuth";
import ApiKeyContext from "./contexts/ApiKeyContext";
import ApiDrivenModel from "./models/ApiDrivenModel";
import Organization from "./models/Organization";
import OrganizationMember from "./models/OrganizationMember";
import IdentityClient from "./api/IdentityClient";
import { DialogContextProvider } from "./hooks/useDialogContext";
import { SidebarContextProvider } from "./hooks/useSidebarContext";
import Associate from "./models/Associate";
import useGlobalStyles from "./hooks/GlobalStyles";
import { Global } from "@emotion/react";
import Catalog from "./models/Catalog";
import { ColorUtil } from "./util/Color.util";
import { readDemoProfileSync } from "./demo/readDemoProfile";
import DemoExitChip from "./demo/components/DemoExitChip";

const PublicLayout = lazy(() => import("./layouts/public/PublicLayout"));
const DemoWrapper = lazy(() => import("./demo/components/DemoWrapper"));
const ProtectedLayout = lazy(() => import("./layouts/protected/ProtectedLayout"));
const PrivateLayout = lazy(() => import("./layouts/private/PrivateLayout"));
const AccountLayout = lazy(() => import("./layouts/account/AccountLayout"));

const PWA = lazy(() => import("./components/PWA"));


function Appin() {
	const [catalog] = React.useContext(CatalogContext);
	const [template, setTemplate] = React.useState(STATIC_CATALOG ? 'private' : false);
	const [emailIsVerified, setEmailIsVerified] = React.useState(false);

	const urlSearchParams = new URLSearchParams(window.location.search);
	const redirect = urlSearchParams.get("redirect")
		&& decodeURIComponent(urlSearchParams.get("redirect"));

	const pathParts = window.location.pathname.trim('/').split('/').filter(hasValue);
	const { currentUser, setCurrentUser, checkAuth, organizationMember, setOrganizationMember, setOrgMemberLoading } = useAuth();

	// Hand-off: when the email-verification flow on bosslogics.live/login
	// redirects here with `?auth=<jwt>`, the user has no password yet
	// (they just verified ownership of the email via the token). Install
	// the JWT as the session immediately so the SetupPasswordModal can
	// collect a password — otherwise they dead-end at /login with nothing
	// to type. Token is stripped from the URL so it doesn't leak via
	// history / referrer / shared screenshots. Runs once on mount.
	React.useEffect(() => {
		const handoffToken = urlSearchParams.get('auth');
		if (!handoffToken || currentUser) return;
		(async () => {
			try {
				await checkAuth(handoffToken);
			} catch (e) {
				console.warn('[auth] verify-handoff token rejected by identity', e);
			} finally {
				const cleaned = new URLSearchParams(window.location.search);
				cleaned.delete('auth');
				cleaned.delete('success');
				const qs = cleaned.toString();
				const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
				window.history.replaceState({}, '', url);
			}
		})();
		// Intentionally run-once on mount — re-firing on every render would
		// loop. The handoffToken check guards against re-entry across
		// remounts when the param is already stripped. CI's ESLint config
		// doesn't include react-hooks, so omitting a disable comment here
		// avoids the "rule not found" error we hit on the prior MR.
	}, []);
	const protectedPageRoots = ['user', 'catalog', 'email-verification'];
	const publicPageRoots = ['login', 'register', 'reset-password', 'pin-login', 'public'];

	React.useEffect(() => {
		const currentUserInfo = JSON.parse(localStorage.getItem('organization_member:data'));
		const branding = currentUserInfo?.organization?.branding;
		let orgForegroundColor = branding?.themes?.header?.foreground?.color || undefined;
		let orgBackgroundColor = branding?.themes?.header?.background?.color || undefined;
		let catalogForegroundColor = catalog?.options?.appearance?.header?.color || catalog?.options?.appearance?.header?.font_color;
		let catalogBackgroundColor = catalog?.options?.appearance?.header?.background_color;
		
		const black_as_number = ColorUtil.ToNumber("#000000");
		const white_as_number = ColorUtil.ToNumber("#ffffff");
		
		// setup perceived assumptions from setting bg and not fg or visa versa
		if (catalogForegroundColor && !catalogBackgroundColor) {
			const foregroundNumber = ColorUtil.ToNumber(catalogForegroundColor);
			if (foregroundNumber === black_as_number) {
				catalogBackgroundColor = "#FFFFFF";
			}
			else if (foregroundNumber === white_as_number) {
				catalogBackgroundColor = "#000000";
			}
		}
		if (catalogBackgroundColor && !catalogForegroundColor) {
			const backgroundNumber = ColorUtil.ToNumber(catalogBackgroundColor);
			if (backgroundNumber === black_as_number) {
				catalogForegroundColor = "#FFFFFF";
			}
			else if (backgroundNumber === white_as_number) {
				catalogForegroundColor = "#000000";
			}
		}
		
		// default any color that's still unset after inference
		catalogForegroundColor = catalogForegroundColor ?? "#000000";
		catalogBackgroundColor = catalogBackgroundColor ?? "#FFFFFF";

		// remove transparency from colors
		catalogForegroundColor = catalogForegroundColor.substring(0, 7);
		catalogBackgroundColor = catalogBackgroundColor.substring(0, 7);

		// substitute demo brand colors before derived CSS-var computation so that
		// darker/lighter vars are derived from the demo palette, not the org palette
		//
		// Color-role mapping:
		//   primaryColor   = brand's header BACKGROUND (the saturated brand
		//                    color for retailers like LBG/Kennedy's, but
		//                    sometimes white for minimalist brands like Fink's).
		//   secondaryColor = brand's header FOREGROUND (the text/logo color
		//                    that reads against primary).
		//
		// Only theme the org strip with the brand — keep the catalog content
		// area on its own defaults. Forcing catalog content to use
		// secondary-as-background (the previous mapping) inverted the page
		// to black-on-white-text for retailers whose primary is white, which
		// looked like a broken dark sliver wrapping the logo + nav.
		// Branded bar on top, neutral content below — the shape every
		// retailer's own site already uses.
		const demoProfile = readDemoProfileSync();
		if (demoProfile) {
			orgForegroundColor = demoProfile.secondaryColor;
			orgBackgroundColor = demoProfile.primaryColor;
		}

		// need these in body because modals and sidebars are created outside #boss container
		const body = document.body;
		body.classList.toggle("retailer-only-branded", Catalog.Is_Retailer_Only_Branded(catalog));
		// assign init styles to :root/html so they can be overridden later in body for retailer only branding
		const root = document.querySelector("html");

		root.style.setProperty("--catalog-foreground-color", catalogForegroundColor);
		root.style.setProperty("--catalog-background-color", catalogBackgroundColor);

		const [darkerCatalogColor, lighterCatalogColor] = ColorUtil.SortColors(catalogForegroundColor, catalogBackgroundColor);
		root.style.setProperty("--catalog-darker-color", darkerCatalogColor);
		root.style.setProperty("--catalog-lighter-color", lighterCatalogColor);

		orgForegroundColor ||= "#000000";
		orgBackgroundColor ||= "#ffffff";
		root.style.setProperty("--org-foreground-color", orgForegroundColor);
		root.style.setProperty("--org-background-color", orgBackgroundColor);

		const [darkerOrgColor, lighterOrgColor] = ColorUtil.SortColors(orgForegroundColor, orgBackgroundColor);

		root.style.setProperty("--org-darker-color", darkerOrgColor);
		root.style.setProperty("--org-lighter-color", lighterOrgColor);

		// Set status bar color for Capacitor native overlay — prefer catalog color, fall back to org darker color, never white
		const statusBarColor = catalogBackgroundColor || darkerOrgColor || "#000";
		root.style.setProperty("--capacitor-statusbar-color", statusBarColor);
	}, [catalog, organizationMember]);


	const API_KEY_CONTEXT = React.useContext(ApiKeyContext);
	ApiDrivenModel.CURRENT_CONTEXT = API_KEY_CONTEXT;

	const fetch_organization_member = async () => {
		const makeOrgDbName = id => id != null ? DB.Make_Name(id, "org") : undefined;
		const makeMemberKey = id => id != null ? ['organizationMember', id] : undefined;

		let localOrgMember = JSON.parse(localStorage.getItem('organization_member:data') || false);
		const orgDbName = makeOrgDbName(localOrgMember?.organization?.id);
		const memberKey = makeMemberKey(localOrgMember?.id);

		let member;
		if (orgDbName && memberKey) {
			const memberData = await DB.Get(orgDbName, memberKey);
			if (memberData?.id) {
				// TODO ... if data should be instantiated, store with class name ...  but then figure out how to do that
				return new OrganizationMember(memberData);
			}
		}

		member = await OrganizationMember.FetchBy({
			filter: [{
				property: '{profile}',
				value: "@profile_id"
			}],
			paths: {
				id: true,
				profile: {
					id: true,
					username: true,
					email: true,
					given_name: true,
					family_name: true,
					pronouns: true,
					picture: true,
					mobile_phone: true,
					last_activity: true,
					public_token: true,
				},
				organization: {
					id: true,
					active: true,
					address: true,
					administrative_area: true,
					country: true,
					formatted_address: true,
					locality: true,
					name: true,
					postal_code: true,
					sub_administrative_area: true,
					logo: true,
					icon: true,
					branding: true,
					features: true,
					permissions: true, // For organization-level permission defaults and role overrides
					price_rules: true, // For Org Admin → Pricing tab
					handle: true,
					verified: true,
					catalog_access: true, // For catalog access master switch and role overrides
					parent: {
						id: true,
						logo: true,
						icon: true,
						branding: true,
						features: true,
						permissions: true,
						handle: true,
					}
				},
				role: {
					id: true,
					is_administrator: true,
					label: true,
					access: true,
				},
				permission_overrides: true, // For member-level permission overrides
				catalog_access_overrides: true, // For member-level catalog access overrides
				verified: true
			},
		}, null, true)

		if (member) {
			// Set for 15 minutes
			DB.Set(makeOrgDbName(member.organization.id), makeMemberKey(member.id), member, 60 * 15);
			return member;
		}

		/*
				TODO !!!!!!!!!
				the Identity authorize call always brings back "organization" as 0
				so we aren't getting an accurate org id to store Associate info
				fix, and put cache back
		 */

		let associate;
		// const associateKey = ['associate', currentUser.email];
		// let associate = await DB.Get(makeOrgDbName(currentUser.organization), associateKey);
		// if (associate) {
		// 	associate = new Associate(associate);
		// }

		if (!associate) {
			associate = await Associate.FetchBy({ filter: [{ property: "email", value: currentUser.email }] });
			// DB.Set(makeOrgDbName(currentUser.organization), associateKey, associate);
		}

		if (!associate?.account?.organization) {
			return;
		}
		const organization = new Organization();
		organization.setProperties({ id: associate.account.organization });
		const { success } = await organization.linkAssociate(associate.id);
		success && window.location.reload();
	}
	
	if (currentUser?.email_verified && window.location.pathname === "/email-verification") {
		// See companion effect below — same redirect, fires on synchronous
		// renders where the user already had email_verified=1 on mount.
		window.location = '/complete-registration';
	}

	const fetchAndSetOrganizationMember = async () => {
		// after registering, there can be a delay before memberships return correctly; give it a few tries
		for (let i = 0; i < 5; i++) {
			const response = await fetch_organization_member().catch(e => {
				// Ignore duplicate member errors
			});

			if (response) {
				response.cleanForSave();
				delete response._system;
				if (response?.id) {
					localStorage.setItem('organization_member:data', JSON.stringify(response));
					setOrganizationMember(response);
					break;
				}
			}
		}
	}

	React.useEffect(() => {
		//if localStorage isn't a proper object, then delete it.
		try {
			let localOrgMember = localStorage.getItem('organization_member:data');
			if (!JSON.parse(localOrgMember) || typeof JSON.parse(localOrgMember) !== 'object') {
				throw new TypeError('error with localStorage')
			}
		} catch (e) {
			localStorage.removeItem('organization_member:data');
		}

		//this usually won't run because localStorage is set upon logging in. 
		//this is just in case localStorage is edited by user, or localStorage is holding invalid JSON object
		if (currentUser) {
			const stored_member = localStorage.getItem("organization_member:data");
			if (stored_member) {
				const parsedMember = JSON.parse(stored_member);
				setOrganizationMember(parsedMember?.id ? parsedMember : undefined);
			}

			fetchAndSetOrganizationMember().finally(() => setOrgMemberLoading(false));
		}

		const getTemplate = () => {
			if (STATIC_CATALOG) {
				return setTemplate("private");
			}
			if (pathParts[0] === 'demo') {
				return setTemplate("demo");
			}
			if (!pathParts.length || publicPageRoots.includes(pathParts[0])) {
				// if (!currentUser || !pathParts.length || publicPageRoots.includes(pathParts[0])) {
				return setTemplate("public");
			}
			if (protectedPageRoots.includes(pathParts[0])) {
				return setTemplate("protected");
			}
			// Single path segment that doesn't match known roots = account code (e.g., /dy/)
			if (pathParts.length === 1) {
				return setTemplate("account");
			}
			return setTemplate("private");
		}
		getTemplate();
		
		if (currentUser && window.location.pathname === "/login") {
			window.location = redirect || '/catalog/select';
		}

	}, [currentUser]);
	
	React.useEffect(() => {
		if (currentUser?.email_verified && window.location.pathname === "/email-verification") {
			// Self-service signups land here without a password. The
			// CompleteRegistration route prompts for one (or passes through
			// if not needed). Honor an explicit ?redirect only if the user
			// already has a password set; until then they need to complete.
			window.location = '/complete-registration';
		}
	}, [currentUser?.email_verified]);

	const getModule = template => {
		switch (template) {
			case 'demo':
				return (<DemoWrapper />);
			case 'public':
				return (<PublicLayout />);
			case 'protected':
				return (<ProtectedLayout />);
			case 'private':
				return (
					<SidebarContextProvider>
						<PrivateLayout />
					</SidebarContextProvider>
				);
			case 'account':
				return (<AccountLayout />);
			default: return null;
		}
	};

	const wrapperClasses = [
		"bossLiveCatalogWrapper",
		catalog?.options?.type && "type-" + catalog.options.type,
		(((catalog !== null) || (template !== "private")) && template)
	];

	const GlobalStyles = useGlobalStyles(catalog?.options?.appearance);

	
	
	if (currentUser && urlSearchParams.get('email-verification') && !emailIsVerified) {
		const verificationCode = urlSearchParams.get('email-verification');
		IdentityClient.post(
			"email_verification_response",
			{
				response: {
					username: currentUser.username,
					verification_token: verificationCode
				}
			}
		).then(response => {
			if (!response.data.success) {
				console.error("Verification failed", response.data);
				// return;
			}
			setEmailIsVerified(true)
			currentUser.email_verified = true;
		})
		.catch(e => {
			// TODO fix ... ignore 404
			console.warn("Bad Response from Verification", e);
		})
		.finally(() => {
			urlSearchParams.delete('email-verification');
			setEmailIsVerified(true)
			currentUser.email_verified = true;
			// Persist the flag so the hard-nav below survives the reload —
			// without this, ProtectedLayout reads the unflipped LS value
			// and bounces /complete-registration back to /email-verification.
			try {
				localStorage.setItem("currentUser", JSON.stringify({ ...currentUser, email_verified: true }));
			} catch (_) { /* private mode — best effort */ }
			// Push through the completion gate after verification so
			// self-service signups can set a password. The route is a
			// no-op for users who already have one.
			window.location = '/complete-registration';
		});

		return <LoadMask show={true} />;
	}

	const auth = urlSearchParams.get("auth");
	if (auth) {
		checkAuth(auth).then(() => {
			// Remove param and reload
			const url = new URL(window.location.href);
			urlSearchParams.delete("auth");
			url.search = urlSearchParams.toString();
			window.location.href = url.toString();
		});
		return (<></>);
	}
	return (
		<PostHogProvider client={posthog}>
			<AnalyticsIdentityBridge />
			<Global styles={GlobalStyles} />
			<div className="_boss" id="_boss">
				<a href="#main-content" className="skip-to-content">Skip to content</a>
				<DialogContextProvider>
					<div className={wrapperClasses.filter(a => a).join(" ")}>
						<Router basename={BASE_PATH} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
							<Suspense fallback={<LoadMask show={true} />}>
								{getModule(template)}
							</Suspense>
							<DemoExitChip />
						</Router>
					</div>
				</DialogContextProvider>
			</div>

			{template && template !== "public" && (
				<React.Suspense fallback={<></>}>
					<PWA />
				</React.Suspense>
			)}
		</PostHogProvider>
	);
}

export default Appin;
